import { of, tap, map, catchError, type Observable } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import { Router, RedirectCommand } from '@angular/router';

import { AuthStore } from './auth-store';

import { type Profile } from '@entities/profile';
import { ProfileRepository } from '@entities/profile';

import { TokenStorage } from './token-storage';
import { AuthRepository } from './auth-repository';
import type {
  Credentials,
  LoginPayload,
  RegistrationInfo,
  InitPasswordResetPayload,
  CompleteRegistrationPayload,
  CompletePasswordResetPayload,
} from './models';

type ResolvedAuthStatus = 'authed' | 'guest';

@Injectable({
  providedIn: 'root',
})
export class AuthFacade {
  private readonly router = inject(Router);

  private readonly store = inject(AuthStore);
  private readonly tokenStorage = inject(TokenStorage);
  private readonly authRepository = inject(AuthRepository);
  private readonly profileRepository = inject(ProfileRepository);

  public resolveAuthStatus(): Observable<ResolvedAuthStatus> {
    if (this.store.profile() !== null) {
      return of('authed');
    }

    if (!this.tokenStorage.hasValidSession()) {
      this.clearSession();
      return of('guest');
    }

    return this.hydrateCurrentUser().pipe(map((profile) => (profile !== null ? 'authed' : 'guest')));
  }

  public login(payload: LoginPayload): Observable<Credentials> {
    return this.authRepository.login(payload).pipe(
      tap((credentials) => {
        this.store.setProfile(credentials.profile);
        this.tokenStorage.set(credentials.token);
      }),
    );
  }

  public logout(): void {
    this.authRepository
      .logout()
      .pipe(catchError(() => of(null)))
      .subscribe();

    this.tokenStorage.clear();
    void this.router.navigateByUrl('/auth/login').then(() => {
      this.store.setGuest();
    });
  }

  public expireSession(): void {
    this.clearSession();
    void this.router.navigateByUrl('/auth/login');
  }

  public refreshToken(): Observable<string> {
    return this.authRepository.refreshToken().pipe(
      tap((credentials) => {
        this.tokenStorage.set(credentials.token);
      }),
      map((credentials) => credentials.token.key),
    );
  }

  public initPasswordReset(payload: InitPasswordResetPayload): Observable<void> {
    return this.authRepository.initPasswordReset(payload);
  }

  public completePasswordReset(payload: CompletePasswordResetPayload): Observable<void> {
    return this.authRepository.completePasswordReset(payload);
  }

  public getRegistrationInfo(confirmationCode: string): Observable<RegistrationInfo> {
    return this.authRepository.getRegistrationInfo(confirmationCode);
  }

  public completeRegistration(payload: CompleteRegistrationPayload): Observable<Credentials> {
    return this.authRepository.completeRegistration(payload).pipe(
      tap((credentials) => {
        this.store.setProfile(credentials.profile);
        this.tokenStorage.set(credentials.token);
      }),
    );
  }

  public redirectForAuthed(): RedirectCommand {
    return new RedirectCommand(this.router.createUrlTree(['/dashboard']), { replaceUrl: true });
  }

  public redirectForGuest(): RedirectCommand {
    return new RedirectCommand(this.router.createUrlTree(['/auth/login']), { replaceUrl: true });
  }

  private hydrateCurrentUser(): Observable<Profile | null> {
    if (!this.tokenStorage.hasValidSession()) {
      return of(null);
    }

    return this.profileRepository.getProfile().pipe(
      tap((profile: Profile) => {
        this.store.setProfile(profile);
      }),
      catchError(() => {
        this.clearSession();
        return of(null);
      }),
    );
  }

  private clearSession(): void {
    this.tokenStorage.clear();
    this.store.setGuest();
  }
}
